import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { updateSearch } from 'core/slices/search';
import { useSelector } from 'react-redux';
import { RootState } from 'types';
import { Search } from '../Search';
import { useAppDispatch } from 'core/store/hooks';
import { ReactNode, useRef } from 'react';
import { SearchType } from 'types/domain/search';

export type SelectItem = {
  name: string;
  component?: ReactNode;
  value: number;
};

export function SelectList({
  label,
  items,
  value,
  onChange,
  search,
  searchType,
  className,
  labelClassName,
  listClassName,
  horizontal,
  sort,
  readOnly,
  ...restOfProps
}: {
  items: SelectItem[];
  value?: number;
  label: string | ReactNode;
  search?: boolean;
  searchType?: SearchType;
  className?: string;
  labelClassName?: string;
  listClassName?: string;
  horizontal?: boolean;
  sort?: boolean;
  readOnly?: boolean;
  onChange: (val) => void;
}) {
  const selected = items.find(v => v.value === value);
  const dispatch = useAppDispatch();
  const searchState = useSelector((state: RootState) => state.core.search);
  const onSelect = item => {
    onChange(item.value);
    //dispatch(updateSearch({ term: null, sort: item.value }));
  };

  const ref = useRef();

  return (
    <Listbox {...restOfProps} disabled={readOnly} value={value} onChange={onSelect}>
      {({ open }) => {
        const showList = open || (searchState.term && searchState.term.length && searchState.searchType === searchType);
        return (
          <div ref={ref} className={`${className} relative`}>
            {search && (!value || showList) ? (
              <div className="h-full flex flex-row flex-1">
                <Search
                  searchType={searchType}
                  className={`${labelClassName} h-full w-full`}
                  placeholder={label}
                  showSort={false}
                />
                <Listbox.Button className="ml-5 relative flex h-full cursor-default border border-gray-100 rounded-lg justify-center items-center shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300">
                  <ChevronDownIcon className="h-5 w-5 " aria-hidden="true" />
                </Listbox.Button>
              </div>
            ) : (
              <Listbox.Button className="h-full w-full relative flex overflow-hidden items-center cursor-defaultfocus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 ">
                <span className={`${labelClassName}`}>{selected?.component || selected?.name || label}</span>
                {!readOnly && (
                  <span className="pointer-events-none inset-y-0 mr-0 flex items-center  ml-1">
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                )}
              </Listbox.Button>
            )}
            {showList && (
              <Listbox.Options
                static={search}
                className={`flex scroll-bar text-left absolute z-front ${
                  horizontal ? 'flex-row' : 'mt-1 flex-col'
                } ${listClassName}  w-full overflow-auto rounded-md bg-white text-gray-800 dark:bg-slate-900/90 dark:text-white py-1 text-base shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
              >
                {items
                  .filter(item =>
                    searchState.term
                      ? item.name && item.name.toLowerCase().includes(searchState.term.toLowerCase())
                      : !!item.name,
                  )
                  .sort((a, b) => (sort ? a.name.localeCompare(b.name) : 1))
                  .map((item, index) => (
                    <Listbox.Option
                      className={({ active }) =>
                        `relative cursor-pointer select-none w-full py-2 pl-4 pr-4 ${
                          active
                            ? 'dark:bg-slate-900 hover:shadow dark:hover:bg-slate-700  hover:bg-howio-purple'
                            : 'dark:text-white text-gray-800'
                        }`
                      }
                      key={index}
                      value={item}
                    >
                      {item.component || item.name}
                    </Listbox.Option>
                  ))}
              </Listbox.Options>
            )}
          </div>
        );
      }}
    </Listbox>
  );
}
